import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Archive, ExternalLink, MoreHorizontal, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { PERMISSIONS } from '@/client/config/permissions';
import { useGate } from '@/client/hooks/useGate';
import { useRunTourReclaimMutation } from '@/client/api/listing-management/virtual-tours.mutation';
import { useDataScopeStore } from '@/client/stores/data-scope';
import { ActionDialog } from './ActionDialog';
import { useVirtualTourReclaimQuery, useVirtualTourCapacityQuery, useVirtualTourReactivationsQuery, VirtualTourModel } from '@/client/api/listing-management/virtual-tours.query';
import { getApiErrorCode, getApiErrorMessage } from '@/client/utils/errors';
import { cn } from '@/lib/utils';
import { fmtDate, LinkedListing, pct, StatusBadge } from './shared';
import { MatterportThumb, ModelPreviewDialog } from './ModelPreview';
import { ArchiveDialog } from './TourActionDialogs';

const ReclaimableTable: React.FC<{ title: string; models: VirtualTourModel[]; onOpenListing: (eid: number) => void; onArchive: (id: string) => void }> = ({
  title,
  models,
  onOpenListing,
  onArchive,
}) => {
  const [preview, setPreview] = useState<VirtualTourModel | null>(null);
  const archive = useGate(PERMISSIONS.virtualTour.archive).allowed;
  return (
  <div className="rounded-lg border border-gray-200 bg-white">
    <div className="border-b border-gray-100 px-4 py-3">
      <h3 className="text-sm font-semibold text-gray-700">
        {title} <span className="text-gray-400">({models.length})</span>
      </h3>
    </div>
    {models.length === 0 ? (
      <p className="px-4 py-6 text-sm text-gray-500">None.</p>
    ) : (
      <table className="min-w-full divide-y divide-gray-100">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-2" />
            <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Model</th>
            <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Id</th>
            <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">State</th>
            <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Visibility</th>
            <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Linked listing</th>
            <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Created</th>
            <th className="px-4 py-2 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {models.map((m) => (
            <tr key={m.id} className="hover:bg-gray-50">
              <td className="px-4 py-2">
                <MatterportThumb id={m.id} alt={m.name ?? m.id} onClick={() => setPreview(m)} />
              </td>
              <td className="px-4 py-2 text-sm text-gray-900">{m.name || '—'}</td>
              <td className="px-4 py-2 font-mono text-xs text-gray-500">{m.id}</td>
              <td className="px-4 py-2">{m.state ? <StatusBadge status={m.state} /> : '—'}</td>
              <td className="px-4 py-2">
                {m.accessVisibility || m.visibility ? <StatusBadge status={m.accessVisibility || m.visibility} /> : <span className="text-sm text-gray-400">—</span>}
              </td>
              <td className="px-4 py-2 text-sm">
                {m.eid ? (
                  <LinkedListing eid={m.eid} name={m.listing_name} onOpen={() => onOpenListing(m.eid as number)} />
                ) : (
                  <span className="text-gray-400">Not linked</span>
                )}
              </td>
              <td className="px-4 py-2 text-sm text-gray-500">{fmtDate(m.created)}</td>
              <td className="px-4 py-2 text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Actions">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem disabled={!archive} onClick={() => onArchive(m.id)}>
                      <Archive /> Archive
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
    <ModelPreviewDialog open={preview != null} onClose={() => setPreview(null)} id={preview?.id ?? ''} name={preview?.name ?? null} />
  </div>
  );
};

const REASON_TEXT: Record<string, string> = {
  listing_not_live: 'Listing not live',
  no_eligible_plan: 'No plan or promotion with a tour',
};

const SlotReclaim: React.FC = () => {
  const navigate = useNavigate();
  const scope = useDataScopeStore((s) => s.scope);
  const { data, isLoading, isError, error } = useVirtualTourReclaimQuery(scope);
  const run = useRunTourReclaimMutation();
  const [open, setOpen] = useState(false);
  const th = 'px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500';

  const cands = data?.candidates ?? [];
  const unstarted = cands.filter((c) => !c.exempt && !c.ineligible_since).length;
  const due = data?.due ?? 0;

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="space-y-2 border-b border-gray-100 px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-gray-700">Slot reclaim</h3>
          <PermissionButton size="sm" variant="outline" code={PERMISSIONS.virtualTour.archive} disabled={!data} onClick={() => setOpen(true)}>
            Run reclaim now
          </PermissionButton>
        </div>
        <p className="text-xs text-gray-500">
          Tour slots are reusable and paid for by promoted listings. When a listing is archived, or its plan or promotion ends, its tour is
          archived to free the slot after a grace period. The link is kept so it can be restored if the listing is promoted again. Tours
          linked by an admin bypass are exempt.
        </p>
        {data && (
          <>
            <p className="text-xs font-medium text-gray-700">
              Automatic reclaim is {data.enabled ? 'ON' : 'OFF'}. Grace period {data.grace_days} day(s), up to {data.max_per_run} per run.
            </p>
            <p className="text-xs text-gray-600">
              {cands.length} candidate(s), {data.due} due, {data.exempt} exempt.
            </p>
            {!data.enabled && (
              <p className="text-xs text-gray-400">Automatic reclaim is off. It is switched on with VIRTUAL_TOUR_AUTO_RECLAIM.</p>
            )}
          </>
        )}
      </div>
      {isError ? (
        <p className="px-4 py-4 text-sm text-red-600">{getApiErrorMessage(error, 'Could not load slot reclaim.')}</p>
      ) : isLoading ? (
        <p className="px-4 py-4 text-sm text-gray-500">Loading…</p>
      ) : cands.length === 0 ? (
        <p className="px-4 py-4 text-sm text-gray-500">No tours are waiting to be reclaimed.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-gray-50">
              <tr>
                <th className={th}>Listing</th>
                <th className={th}>Reason</th>
                <th className={th}>Ineligible since</th>
                <th className={th}>Due</th>
                <th className="px-4 py-2 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-sm">
              {cands.map((c) => (
                <tr key={c.vtid}>
                  <td className="px-4 py-2">
                    {c.eid ? (
                      <LinkedListing eid={c.eid} name={c.listing_name} onOpen={() => navigate(`/admin/listings/${c.eid}`)} />
                    ) : (
                      '—'
                    )}
                    <span className="ml-2 inline-flex gap-1 align-middle">
                      {c.exempt && <span className="rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold text-violet-700">Exempt (bypass)</span>}
                      {!!c.tester && <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-600">Test listing</span>}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-gray-600">{REASON_TEXT[c.reason] ?? c.reason}</td>
                  <td className="px-4 py-2 text-gray-500">{fmtDate(c.ineligible_since)}</td>
                  <td className="px-4 py-2 text-gray-500">{c.exempt ? '—' : c.due_at ? fmtDate(c.due_at) : 'clock not started'}</td>
                  <td className="px-4 py-2 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Actions">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem asChild>
                          <Link to={`/admin/listings/virtual-tours?tab=tours&tour=${c.vtid}`}>
                            <ExternalLink /> Open tour
                          </Link>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ActionDialog
        open={open}
        onClose={() => setOpen(false)}
        title="Run slot reclaim now?"
        description={`Starts the grace clock for ${unstarted} tour(s) and archives ${due} tour(s) that are past it. Archived tours are hidden on their listings and the sellers are notified.`}
        confirmLabel="Run reclaim"
        successMessage="Reclaim run finished."
        submit={async (reason) => {
          const res = await run.mutateAsync({ reason });
          const r = res?.result;
          toast.info(`Archived ${r?.reclaimed?.length ?? 0}, failed ${r?.failed?.length ?? 0}`);
        }}
      />
    </div>
  );
};

export const CapacityTab: React.FC = () => {
  const navigate = useNavigate();
  const [refresh, setRefresh] = useState(false);
  const [archiveId, setArchiveId] = useState<string | null>(null);
  const reactivations = useVirtualTourReactivationsQuery().data;
  const { data, isLoading, isFetching, isError, error, refetch } = useVirtualTourCapacityQuery(refresh);
  const unavailable = isError && getApiErrorCode(error) === 'provider_unavailable';

  const handleRefresh = () => {
    setRefresh(true);
    refetch().finally(() => setRefresh(false));
  };

  const openListing = (eid: number) => navigate(`/admin/listings/${eid}`);

  if (unavailable) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-6 text-sm text-amber-800">
        Matterport unreachable — capacity data could not be loaded.
        <Button variant="outline" size="sm" className="ml-3" onClick={handleRefresh}>
          <RefreshCw className="mr-2 h-3.5 w-3.5" /> Retry
        </Button>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-6 text-sm text-red-600">
        {getApiErrorMessage(error, 'Could not load capacity.')}
      </div>
    );
  }

  const util = data?.utilisation ?? 0;
  const warn = util >= 0.9;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end">
        <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isFetching}>
          <RefreshCw className={cn('mr-2 h-4 w-4', isFetching && 'animate-spin')} /> Refresh
        </Button>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <div className="flex items-baseline justify-between">
          <h3 className="text-sm font-semibold text-gray-700">Active spaces</h3>
          <span className={cn('text-sm font-semibold', warn ? 'text-amber-600' : 'text-gray-500')}>
            {pct(util)} used
          </span>
        </div>
        <p className="mt-1 text-3xl font-bold text-gray-900">
          {isLoading ? '—' : data?.active ?? 0} <span className="text-base font-normal text-gray-400">/ {data?.cap ?? '—'}</span>
        </p>
        <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-gray-100">
          <div
            className={cn('h-full rounded-full', warn ? 'bg-amber-500' : 'bg-emerald-500')}
            style={{ width: `${Math.min(100, Math.round(util * 100))}%` }}
          />
        </div>
        <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-xs text-gray-400">Archived</span>
            <p className="font-medium">{data?.archived ?? '—'}</p>
          </div>
          <div>
            <span className="text-xs text-gray-400">Free reactivations</span>
            <p className="font-medium">
              {data?.free_reactivations ?? '—'}
              {reactivations && <span className="ml-1 text-xs font-normal text-gray-400">({reactivations.remaining} remaining)</span>}
            </p>
          </div>
          <div>
            <span className="text-xs text-gray-400">Renewal date</span>
            <p className="font-medium">{fmtDate(data?.renewal_date)}</p>
          </div>
        </div>
      </div>

      <ReclaimableTable
        title="Active on non-live listings"
        models={data?.reclaimable?.on_non_live_listings ?? []}
        onOpenListing={openListing}
        onArchive={setArchiveId}
      />
      <ReclaimableTable
        title="Unlinked models"
        models={data?.reclaimable?.unlinked ?? []}
        onOpenListing={openListing}
        onArchive={setArchiveId}
      />
      <SlotReclaim />
      <ArchiveDialog open={archiveId != null} onClose={() => setArchiveId(null)} target={{ model_id: archiveId }} />
    </div>
  );
};

export default CapacityTab;
